import React from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, CheckCircle, Info, XCircle } from 'lucide-react';

const ConfirmationModal = ({
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    confirmText = "Confirm",
    cancelText = "Cancel",
    type = "info",
    showCancel = true
}) => {
    React.useEffect(() => {
        const handleEsc = (event) => {
            if (event.keyCode === 27) onClose();
        };
        if (isOpen) {
            window.addEventListener('keydown', handleEsc);
        }
        return () => window.removeEventListener('keydown', handleEsc);
    }, [isOpen, onClose]);

    if (!isOpen) return null;


    const getThemeConfig = () => {
        switch (type) {
            case 'danger':
                return {
                    icon: <XCircle className="w-10 h-10 text-rose-500" strokeWidth={1.5} />,
                    bgIcon: 'bg-rose-50',
                    borderColor: 'border-rose-100',
                    titleColor: 'text-gray-900',
                    buttonBg: 'bg-rose-600 hover:bg-rose-700',
                    ringColor: 'focus:ring-rose-200'
                };
            case 'success':
                return {
                    icon: <CheckCircle className="w-10 h-10 text-emerald-500" strokeWidth={1.5} />,
                    bgIcon: 'bg-emerald-50',
                    borderColor: 'border-emerald-100',
                    titleColor: 'text-gray-900',
                    buttonBg: 'bg-[#067978] hover:bg-[#056362]',
                    ringColor: 'focus:ring-teal-200'
                };
            case 'warning':
                return {
                    icon: <AlertTriangle className="w-10 h-10 text-amber-500" strokeWidth={1.5} />,
                    bgIcon: 'bg-amber-50',
                    borderColor: 'border-amber-100',
                    titleColor: 'text-gray-900',
                    buttonBg: 'bg-amber-500 hover:bg-amber-600',
                    ringColor: 'focus:ring-amber-200'
                };
            default:
                return {
                    icon: <Info className="w-10 h-10 text-[#067978]" strokeWidth={1.5} />,
                    bgIcon: 'bg-teal-50',
                    borderColor: 'border-teal-100',
                    titleColor: 'text-gray-900',
                    buttonBg: 'bg-[#067978] hover:bg-[#056362]',
                    ringColor: 'focus:ring-cyan-200'
                };
        }
    };

    const theme = getThemeConfig();

    return createPortal(
        <div className="fixed inset-0 z-[200000] flex items-center justify-center p-4" role="dialog" aria-modal="true">
            <div
                className="fixed inset-0 bg-gray-950/30 backdrop-blur-md transition-all duration-500"
                onClick={onClose}
            />

            <div className="relative w-full max-w-sm bg-white rounded-3xl shadow-2xl transform transition-all animate-modal-pop overflow-hidden border border-gray-100 font-sans">
                <div className="relative px-6 py-10 flex flex-col items-center text-center">
                    <div className={`mb-6 p-4 rounded-2xl ${theme.bgIcon} flex items-center justify-center`}>
                        {theme.icon}
                    </div>

                    <h3 className={`text-xl font-extrabold tracking-tight ${theme.titleColor} mb-2 uppercase`}>
                        {title}
                    </h3>

                    <p className="text-gray-500 leading-relaxed font-semibold text-sm mb-8 px-4">
                        {message}
                    </p>

                    <div className="w-full flex items-center justify-center gap-4 px-4">
                        {showCancel && (
                            <button
                                type="button"
                                className="flex-1 py-3 px-4 rounded-xl text-gray-400 font-extrabold uppercase tracking-widest text-[10px] hover:text-gray-600 transition-all active:scale-95"
                                onClick={onClose}
                            >
                                {cancelText}
                            </button>
                        )}
                        <button
                            type="button"
                            className={`flex-1 py-3 px-4 rounded-xl text-white font-extrabold uppercase tracking-widest text-[10px] shadow-lg shadow-black/5 transform transition-all active:scale-95 focus:outline-none focus:ring-2 ${theme.ringColor} ${theme.buttonBg}`}
                            onClick={() => {
                                onConfirm();
                                onClose();
                            }}
                        >
                            {confirmText}
                        </button>
                    </div>
                </div>

                <style>{`
                    @keyframes modal-pop {
                        0% { opacity: 0; transform: scale(0.95) translateY(10px); }
                        100% { opacity: 1; transform: scale(1) translateY(0); }
                    }
                    .animate-modal-pop {
                        animation: modal-pop 0.3s ease-out forwards;
                    }
                `}</style>
            </div>
        </div>,
        document.body
    );
};

export default ConfirmationModal;
